import {
    TaskEvalResults,
    useStore,
    useStoreUI,
    useTasksStore
} from '@assets/model/Store';
import { observer } from 'mobx-react';
import { Split } from '../Split/Split';
import { Flex } from '../UI/Flex/Flex';
import { TaskChatPanel } from './TaskChatPanel/TaskChatPanel';
import * as style from './TaskPage.module.less';

import icon_checkmark from './icons/checkmark.svg';
import icon_circle from './icons/circle.svg';
import icon_eval_fail from './icons/icon_eval_fail.svg';
import icon_eval_passed from './icons/icon_eval_pass.svg';
import icon_paused from './icons/paused.svg';
import icon_working from './icons/working.svg';

import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { onValue, ref } from 'firebase/database';
import { useEffect, useState } from 'react';
import { ModalEditEvals } from '../ModalEditEvals/ModalEditEvals';
import { ModalShowEval } from '../ModalShowEval/ModalShowEval';
import { ModalShowVariable } from '../ModalShowVariable/ModalShowVariable';
import { TaskScore } from './TaskScore/TaskScore';
import { Dropdown } from 'react-bootstrap';
import classNames from 'classnames';

interface TaskPageProps {
    onClickBack?: () => void;
    manualSetTaskId?: string;
}

export const TaskPage = observer((props: TaskPageProps) => {
    const { onClickBack, manualSetTaskId } = props;
    const store = useStore();
    const taskId =
        manualSetTaskId || store.selectedTaskId || 'identify_industry';
    const taskStore = useTasksStore();
    const query = store.query;
    const active_task_id = taskId;
    const task = taskStore.get_task_definition(active_task_id);

    const reportId = store.selectedReportId;

    const [storedTasksVars, setStoredVariables] = useState(null);
    const [taskEvalsResults, setTaskEvalResults] = useState<TaskEvalResults[]>(
        []
    );

    const onClickClear = () => {
        const ui = useStoreUI();
        if (ui.chatWaitingForResponse) return;

        const areYouSure = window.confirm(
            'Are you sure you want to clear the chat history? This action cannot be undone.'
        );

        if (!areYouSure) return;

        const fb = getFirebase();
        fb.flowtask_add_clear_job(reportId, taskId);
        // The backend's clear job removes the canonical variable, chat
        // history, task_state and eval results — but doesn't know about
        // the v2 paths (staging proposal, its metadata, file snapshot),
        // so we wipe those here to match the "reset everything" intent.
        if (task?.save_to) {
            fb.flowtask_clear_v2_state(reportId, taskId, task.save_to);
        }
    };

    useEffect(
        function fetchTaskContent() {
            const path = `/flowtasks/vars/${reportId}`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const data = snapshot.val();
                console.log('TaskPage: fetched stored variables:', data);
                setStoredVariables(data);
            });

            return () => {
                unsub();
            };
        },
        [active_task_id]
    );

    useEffect(
        function fetchTaskEvals() {
            const path = `/flowtasks/eval_results/${reportId}/${active_task_id}`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const data = snapshot.val();
                console.log('TaskPage: fetched evals variables:', data);
                setTaskEvalResults(data ?? []);
            });

            return () => {
                unsub();
            };
        },
        [active_task_id]
    );

    if (!task) {
        return <div>Task not found: {active_task_id}</div>;
    }

    const tasksState = store.activeReportTasksState;
    const taskState = tasksState.getTaskQuery(active_task_id);

    const active_step = taskState.activeStep;
    const is_complete = taskState.isComplete;
    // Auto-trigger for the comparator now lives at the DealView level
    // (useAutoTriggerComparator) — covers entry, tab visibility, window focus
    // for all modules, not just the currently-open one.

    const var_exists = (storedTasksVars ?? {}).hasOwnProperty(task.save_to);
    const onShowVariable = () => {
        if (!var_exists) return;
        const uiStore = useStoreUI();
        uiStore.modal = {
            title: 'Variable: ' + task.save_to,
            el: (
                <ModalShowVariable
                    variableName={task.save_to}
                    variableValue={storedTasksVars[task.save_to]}
                />
            )
        };
    };
    const prework_vars_list = task.prerequisite_variable ?? [
        { name: '', display: 'None' }
    ];
    const prework_vars = prework_vars_list.map((var_obj, index) => {
        const is_var_ready = !taskState.isLocked;
        var icon = is_var_ready ? icon_checkmark : icon_circle;
        return (
            <Flex alignItems="center" key={'prework_' + index}>
                <img src={icon} className={style.line_icon_smaller} />
                <div className={style.line_text}>{var_obj.display}</div>
            </Flex>
        );
    });

    const inner_steps = task.inner_steps_names.map((step_name, index) => {
        const isCurrentStep = index + 1 == Number(active_step);
        const isDone = index + 1 < Number(active_step) || is_complete;

        var icon = null;
        if (isCurrentStep && !is_complete) {
            icon =
                taskState.status == 'in_progress' ? icon_working : icon_paused;
        } else {
            icon = isDone ? icon_checkmark : icon_circle;
        }

        return (
            <Flex alignItems="center" key={'step_' + index}>
                <img src={icon} className={style.line_icon_smaller} />
                <div className={style.line_text}>{step_name}</div>
            </Flex>
        );
    });

    const evals = { ...taskStore.global_eval_rules, ...task.evals };
    const eval_list = [];
    var taskScore = -1;
    if (taskEvalsResults) {
        taskScore = Math.round(
            (taskEvalsResults.filter(e => e.is_valid).length /
                taskEvalsResults.length) *
            100
        );
    }

    for (const eval_id in evals) {
        const evalItem = evals[eval_id];
        const evalResult = taskEvalsResults.find(e => e.id === eval_id);
        var icon = icon_circle;
        var iconClass = style.line_icon_smaller_eval;
        if (evalResult) {
            icon = evalResult?.is_valid ? icon_eval_passed : icon_eval_fail;
            var iconClass = style.line_icon;
        }

        const eval_el = (
            <Flex
                direction="row"
                key={'eval_' + eval_id}
                alignItems="center"
                justify="center"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                    const uiStore = useStoreUI();
                    uiStore.modal = {
                        title: `Evaluation: ${evalItem.name}`,
                        el: (
                            <ModalShowEval
                                reason={
                                    evalResult?.reason ||
                                    'No details available.'
                                }
                                is_valid={evalResult?.is_valid || false}
                            />
                        )
                    };
                }}
            >
                <div className={style.line_text}>{evalItem.name}</div>
                <div style={{ flexGrow: 1 }}></div>
                <img src={icon} className={iconClass} />
            </Flex>
        );
        eval_list.push(eval_el);
    }

    return (
        <Split spaceBetween={0} layoutPercent={[25, 75]}>
            <Flex direction="column" fillCell={true}>
                <Flex
                    direction="column"
                    className={style.round_border_container}
                    fillCell={true}
                >
                    <Flex className={style.back_to_workstreams} direction="row">
                        <button
                            className={style.back_button}
                            onClick={() => {
                                if (onClickBack) onClickBack();
                            }}
                        >
                            <i
                                className="bi bi-arrow-left"
                                style={{
                                    color: 'white',
                                    fontSize: '14px',
                                    position: 'relative',
                                    top: '-1px'
                                }}
                            ></i>
                        </button>

                        <div
                            style={{
                                flexGrow: 1,
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden'
                            }}
                        >
                            {task.name}
                        </div>
                        <Dropdown>
                            <Dropdown.Toggle className="dropdown-toggle-overide">
                                <i
                                    className={classNames(
                                        'bi bi-three-dots-vertical'
                                    )}
                                    style={{
                                        color: 'white',
                                        fontSize: '14px',
                                        position: 'relative'
                                    }}
                                ></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item>
                                    <div
                                        className={style.dropdownItem}
                                        onClick={onClickClear}
                                    >
                                        Restart chat & Delete conversation
                                    </div>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Flex>

                    <Flex direction="column" className={style.prework_list}>
                        <div className={style.inner_title}>Pre-work items</div>
                        {prework_vars}
                    </Flex>

                    <Flex direction="column" className={style.steps_list}>
                        <div className={style.inner_title}>Work items</div>
                        {inner_steps}
                    </Flex>
                    <Flex direction="column" className={style.steps_list_eval}>
                        <Flex direction="row">
                            <div className={style.inner_title}>Evaluations</div>
                            <div style={{ flexGrow: 1 }}></div>
                            <div className={style.evalPercentBox}>
                                <TaskScore score={taskScore} />
                            </div>
                        </Flex>
                        {eval_list}
                        <Flex direction="row" style={{ marginTop: '10px' }}>
                            <div style={{ flexGrow: 1 }}></div>
                            <div
                                className="hover-button"
                                onClick={() => {
                                    const ui = useStoreUI();
                                    ui.modal = {
                                        title: 'Edit Evals',
                                        el: (
                                            <ModalEditEvals
                                                taskId={active_task_id}
                                            />
                                        )
                                    };
                                }}
                            >
                                <i
                                    className="bi bi-pencil-square"
                                    style={{
                                        paddingRight: '12px'
                                    }}
                                ></i>
                            </div>
                        </Flex>
                    </Flex>

                    <div style={{ flexGrow: 1 }}></div>
                    <div className={style.saved_outputs}>
                        <Flex
                            fillCell={true}
                            style={{
                                padding: 5,
                                paddingTop: 8
                            }}
                        >
                            <div
                                className={style.is_var_saved}
                                onClick={() => {
                                    onShowVariable();
                                }}
                            >
                                {var_exists ? '1' : '0'}
                            </div>
                            <div style={{ flexGrow: 1 }}></div>
                            <div
                                className={style.line_text}
                                style={{ paddingRight: '3px' }}
                            >
                                Saved Outputs
                            </div>
                        </Flex>
                    </div>
                </Flex>
            </Flex>

            <TaskChatPanel
                taskId={active_task_id}
                isLocked={taskState.isLocked}
            />
        </Split>
    );
});
